// =============================================================================
// FEATURE FLAGS: opt-in gates for in-progress builds
//
// Living Corpus v2 ("The Studio", real-time duplex voice) ships BEHIND a flag
// with graceful degradation, so the app always works: flag off → the hardened
// turn-based path (tier 2/3) is unchanged. STUDIO_V2 is the single gate every
// new Studio surface checks before mounting.
//
// Resolution priority (first match wins), mirroring the depot_manager feature
// flag pattern but single-user-simple:
//   1) URL param      ?studio=1 / ?studio=0   (per-tab override, easy to demo)
//   2) localStorage   careeropt.ff.STUDIO_V2 = "1" | "0"   (sticky per device)
//   3) env (build)    VITE_FF_STUDIO_V2 = "true" | "false"
//   4) default        STUDIO_V2 ON — v2 ("The Studio") is the live experience,
//                     not an opt-in A/B. ?studio=0 is the manual kill switch.
//
// A URL param also PERSISTS to localStorage so a shared link sticks across
// reloads. No network, no provider: a plain function read anywhere.
// =============================================================================

/** The canonical flag names. Frozen strings; the 4 teams check these verbatim. */
export const FEATURE_FLAGS = {
  /** Living Corpus v2 / The Studio: real-time duplex voice + ambient canvas. */
  STUDIO_V2: 'STUDIO_V2',
  /** Standalone tactile-feedback style guide (/corpus-live). Mock-only, fully
   *  isolated from the live loop; default OFF. A design surface to approve the
   *  card live-state + trim language before any of it is wired into the canvas. */
  CORPUS_LIVE_STYLEGUIDE: 'CORPUS_LIVE_STYLEGUIDE',
  /** Device-side resilience: an on-device turn cache (nothing said is lost across
   *  a drop/reload) + a roving session cap (resets as the conversation continues)
   *  + rollover-as-digest. Additive + OFF by default; opt in with ?resilience=1. */
  STUDIO_RESILIENCE: 'STUDIO_RESILIENCE',
  /** Career DNA through-line viz: the horizontal career-spine visualization on
   *  /dna (timeline of eras + the woven through-line + the gold you undersell +
   *  a readiness chip). V1, refine later; OFF by default — opt in with
   *  ?throughline=1 — so shipping is zero-risk until it's flipped on. */
  DNA_THROUGH_LINE: 'DNA_THROUGH_LINE',
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/** The URL param + localStorage key for each flag. */
const FLAG_KEYS: Record<FeatureFlag, { param: string; storage: string; env: string }> = {
  STUDIO_V2: {
    param: 'studio',
    storage: 'careeropt.ff.STUDIO_V2',
    env: 'VITE_FF_STUDIO_V2',
  },
  CORPUS_LIVE_STYLEGUIDE: {
    param: 'corpusLive',
    storage: 'careeropt.ff.CORPUS_LIVE_STYLEGUIDE',
    env: 'VITE_FF_CORPUS_LIVE_STYLEGUIDE',
  },
  STUDIO_RESILIENCE: {
    param: 'resilience',
    storage: 'careeropt.ff.STUDIO_RESILIENCE',
    env: 'VITE_FF_STUDIO_RESILIENCE',
  },
  DNA_THROUGH_LINE: {
    param: 'throughline',
    storage: 'careeropt.ff.DNA_THROUGH_LINE',
    env: 'VITE_FF_DNA_THROUGH_LINE',
  },
};

// Per-flag default when nothing overrides. STUDIO_V2 is ON: v2 ("The Studio") is
// the live experience, not an opt-in A/B. ?studio=0 / localStorage / a build env
// of "false" are the only ways off (an ordered rollback), never the default.
const FLAG_DEFAULTS: Record<FeatureFlag, boolean> = {
  STUDIO_V2: true,
  // OFF by default: opt in with ?corpusLive=1 — a mock-only design surface.
  CORPUS_LIVE_STYLEGUIDE: false,
  // ON: device-side resilience (turn cache + roving cap) is live. ?resilience=0
  // is the manual kill switch.
  STUDIO_RESILIENCE: true,
  // OFF by default: the through-line viz is V1, shipped dark. ?throughline=1
  // (or localStorage / a build env of "true") turns it on; flipping the default
  // here ships it to everyone.
  DNA_THROUGH_LINE: false,
};

function truthy(v: string | null | undefined): boolean {
  if (v == null) return false;
  const s = v.trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'on' || s === 'yes';
}

function falsy(v: string | null | undefined): boolean {
  if (v == null) return false;
  const s = v.trim().toLowerCase();
  return s === '0' || s === 'false' || s === 'off' || s === 'no';
}

/**
 * Read a feature flag. Safe in SSR / non-browser contexts (guards `window`).
 * A URL param wins and is persisted to localStorage so it sticks across reloads.
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  const keys = FLAG_KEYS[flag];

  // 1) URL param (and persist it so a demo link survives a reload).
  if (typeof window !== 'undefined' && window.location) {
    const param = new URLSearchParams(window.location.search).get(keys.param);
    if (truthy(param) || falsy(param)) {
      const on = truthy(param);
      try {
        window.localStorage.setItem(keys.storage, on ? '1' : '0');
      } catch {
        // localStorage can throw in private mode; the param still applies this load.
      }
      return on;
    }
  }

  // 2) localStorage (sticky per device).
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(keys.storage);
      if (truthy(stored)) return true;
      if (falsy(stored)) return false;
    } catch {
      // ignore
    }
  }

  // 3) build-time env var (can force on OR off).
  const env = (import.meta.env as Record<string, string | undefined>)[keys.env];
  if (truthy(env)) return true;
  if (falsy(env)) return false;

  // 4) per-flag default. STUDIO_V2 is ON: v2 is the live experience, not opt-in.
  return FLAG_DEFAULTS[flag] ?? false;
}

/** Convenience reader for the one flag that exists today. */
export function isStudioV2Enabled(): boolean {
  return isFeatureEnabled(FEATURE_FLAGS.STUDIO_V2);
}

/** Convenience reader for the Career DNA through-line viz gate (OFF by default). */
export function isDnaThroughLineEnabled(): boolean {
  return isFeatureEnabled(FEATURE_FLAGS.DNA_THROUGH_LINE);
}
